import { useCallback, useMemo, useState } from 'react'

import { Blend, PlusIcon, Pyramid, Trash, Wallpaper, XIcon } from 'lucide-react'
import { Separator, DefaultPopover, Button, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DefaultTooltip } from 'twyne-new.ui'

import { useCraftProp } from '../../utils'
import PaletteColors from '../Color/Palette'
import GradientPicker from '../components/gradient/Picker'
import GradientPreview from '../components/gradient/Preview'

import { BackgroundColorPreview } from './color'
import { Image, ImagePreview } from './image'
import { SelectComponent, SelectPreview } from './select'

export default function MultiColorProperty() {
  const { value: color, setValue: setColor } = useCraftProp('background.color')
  const { value: gradient, setValue: setGradient } = useCraftProp('background.gradient')
  const { value: image, setValue: setImage } = useCraftProp('background.image')
  const { value: blendMode, setValue: setBlendMode } = useCraftProp('background.blendMode')
  const { value: origin, setValue: setOrigin } = useCraftProp('background.origin')
  const { value: mixBlendMode, setValue: setMixBlendMode } = useCraftProp('background.mixBlendMode')

  const craft = useMemo(() => ({
    color: { set: setColor, value: color },
    gradient: { set: setGradient, value: gradient },
    image: { set: setImage, value: image },
    blendMode: { set: setBlendMode, value: blendMode },
    origin: { set: setOrigin, value: origin },
    mixBlendMode: { set: setMixBlendMode, value: mixBlendMode },
  }), [setColor, setGradient, setImage, setBlendMode, setOrigin, setMixBlendMode, color, gradient, image, blendMode, origin, mixBlendMode])

  const handleAdd = useCallback(mode => {
    const setter = craft[mode]?.set
    if (setter) setter({})
  }, [craft])

  const handleDelete = useCallback(mode => {
    const setter = craft[mode]?.set
    if (setter) setter('')
  }, [craft])

  const values = useMemo(() => Object.fromEntries(
    Object.entries(craft).map(([key, { value }]) => [key, value]),
  ), [craft])

  const items = useMemo(() => {
    const items = OPTIONS.filter(option => values[option.value])
    return items.length ? items : [OPTIONS.find(option => option.value === 'color')]
  }, [values])

  return <>
    <div className='flex flex-col gap-2 mb-2'>
      {items.map(({ value, ...props }) => <Item
        key={value}
        onDelete={handleDelete}
        mode={value}
        values={values}
        name={`background.${value}`}
        {...props}
      />)}
    </div>
    <AddButton values={values} handleAdd={handleAdd} />
  </>
}

const AddButton = ({ values, handleAdd }) => {
  const optionsDropdown = useMemo(() => OPTIONS.filter(option => !values[option.value]), [values])

  const dropDownOptions = useMemo(() => {
    const options = []

    optionsDropdown.forEach(({ value, label, hint }) => {
      if (!values[value]) {
        options.push({ label, value, hint })
      }
    })

    return options
  }, [values, optionsDropdown])

  return <>
    <div className='flex justify-end'>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size='sm' disabled={!optionsDropdown.length} variant='light'> <PlusIcon size='14' /> </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {dropDownOptions.map(({ label, value, hint }) => <>
            <DefaultTooltip content={hint} triggerProps={{ asChild: true }} contentProps={{ className: 'max-w-40' }} side='left'>
              <DropdownMenuItem key={value} onClick={() => handleAdd(value)}>
                {label}
              </DropdownMenuItem>
            </DefaultTooltip>
          </>)}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </>
}

const Item = ({ preview: Preview, mode, values, onDelete, ...props }) => <>
  <PoppoverWrapper mode={mode} values={values} {...props}>
    <div className='flex gap-2 justify-between'>
      <div className='w-full'>
        <Preview mode={mode} {...values} {...props} />
      </div>
      <Button variant='light' size='sm' onClick={() => onDelete(mode)}>
        <Trash size='14' />
      </Button>
    </div>
  </PoppoverWrapper>
</>

const PoppoverWrapper = ({ mode, children, label, component: Component, name, popoverSize, ...props }) => {
  const [open, setOpen] = useState(false)

  return <>
    <DefaultPopover
      trigger={children}
      triggerProps={{ asChild: true }}
      contentProps={{ side: 'left', align: 'center', className: `w-${popoverSize || '72'}` }}
      wrapperProps={{ open: open, onOpenChange: setOpen }}
    >
      <div className='flex justify-between items-center'>
        {label}
        <XIcon size='16' className='cursor-pointer' onClick={() => setOpen(false)} />
      </div>
      <Separator className='m-0 my-2' />

      <Component mode={mode} label={label} {...props} name={name} onClose={() => setOpen(false)} />
    </DefaultPopover>
  </>
}

const ORIGIN_OPTIONS = [
  { label: 'Content', value: 'bg-origin-content' },
  { label: 'Border', value: 'bg-origin-border' },
  { label: 'Padding', value: 'bg-origin-padding' },
]

const BLEND_OPTIONS = [
  { label: 'Normal', value: 'bg-blend-normal' },
  { label: 'Multiply', value: 'bg-blend-multiply' },
  { label: 'Screen', value: 'bg-blend-screen' },
  { label: 'Overlay', value: 'bg-blend-overlay' },
  { label: 'Darken', value: 'bg-blend-darken' },
  { label: 'Lighten', value: 'bg-blend-lighten' },
  { label: 'Color Dodge', value: 'bg-blend-color-dodge' },
  { label: 'Color Burn', value: 'bg-blend-color-burn' },
  { label: 'Hard Light', value: 'bg-blend-hard-light' },
  { label: 'Soft Light', value: 'bg-blend-soft-light' },
  { label: 'Difference', value: 'bg-blend-difference' },
  { label: 'Exclusion', value: 'bg-blend-exclusion' },
  { label: 'Hue', value: 'bg-blend-hue' },
  { label: 'Saturation', value: 'bg-blend-saturation' },
  { label: 'Color', value: 'bg-blend-color' },
  { label: 'Luminosity', value: 'bg-blend-luminosity' },
]

const MIX_BLEND_OPTIONS = [
  { label: 'Normal', value: 'mix-blend-normal' },
  { label: 'Multiply', value: 'mix-blend-multiply' },
  { label: 'Screen', value: 'mix-blend-screen' },
  { label: 'Overlay', value: 'mix-blend-overlay' },
  { label: 'Darken', value: 'mix-blend-darken' },
  { label: 'Lighten', value: 'mix-blend-lighten' },
  { label: 'Color Dodge', value: 'mix-blend-color-dodge' },
  { label: 'Color Burn', value: 'mix-blend-color-burn' },
  { label: 'Hard Light', value: 'mix-blend-hard-light' },
  { label: 'Soft Light', value: 'mix-blend-soft-light' },
  { label: 'Difference', value: 'mix-blend-difference' },
  { label: 'Exclusion', value: 'mix-blend-exclusion' },
  { label: 'Hue', value: 'mix-blend-hue' },
  { label: 'Saturation', value: 'mix-blend-saturation' },
  { label: 'Color', value: 'mix-blend-color' },
  { label: 'Luminosity', value: 'mix-blend-luminosity' },
  { label: 'Plus Darker', value: 'mix-blend-plus-darker' },
  { label: 'Plus Lighter', value: 'mix-blend-plus-lighter' },
]

const OPTIONS = [
  { component: PaletteColors, preview: BackgroundColorPreview, value: 'color', label: 'Color' },
  { component: Image, preview: ImagePreview, value: 'image', label: 'Image' },
  { component: GradientPicker, preview: GradientPreview, value: 'gradient', label: 'Gradient', popoverSize: '96' },
  { component: SelectComponent, preview: SelectPreview, value: 'blendMode', label: 'Blend Mode', options: BLEND_OPTIONS, icon: <Wallpaper size={14} />, hint: 'Controls how multiple background layers blend with each other.' },
  { component: SelectComponent, preview: SelectPreview, value: 'origin', label: 'Origin', options: ORIGIN_OPTIONS, icon: <Pyramid size={14} /> },
  { component: SelectComponent, preview: SelectPreview, value: 'mixBlendMode', label: 'Mix Blend Mode', options: MIX_BLEND_OPTIONS, icon: <Blend size={14} />, hint: 'Controls how an element blends with the background behind it.' },
]
